import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { 
  Crown, GraduationCap, CheckCircle, CreditCard, 
  Wallet, Globe, Users, ArrowRight, Star, Shield,
  MapPin, Copy, Check, ExternalLink, AlertCircle, Hash, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OAR_LOGO = "https://customer-assets.emergentagent.com/job_a27b2f16-dbd8-4903-b7cf-e0c3f4166774/artifacts/zuafya4h_LOGO%20OAR%20jpeg.jpg";

// Payment Links
const PAYSTACK_STANDARD = "https://paystack.com/buy/membre-standardetudiant-lcczjc";
const PAYSTACK_PREMIUM = "https://paystack.com/buy/membre-premium-prioritaire-wnqads";

// USDT TRC20 Wallet
const CRYPTO_WALLET = "TTwGsFAGNWWYhUxCJDJ59NJW8qKM3ToNsc";
const CRYPTO_QR_CODE = "https://customer-assets.emergentagent.com/job_pan-africa-hub/artifacts/u1fy7g0j_WhatsApp%20Image%202026-02-25%20at%2014.19.28.jpeg";

const africanCountries = [
  "Afrique du Sud", "Algérie", "Angola", "Bénin", "Botswana", "Burkina Faso",
  "Burundi", "Cameroun", "Cap-Vert", "Centrafrique", "Comores", "Congo",
  "Côte d'Ivoire", "Djibouti", "Égypte", "Érythrée", "Eswatini", "Éthiopie",
  "Gabon", "Gambie", "Ghana", "Guinée", "Guinée-Bissau", "Guinée équatoriale",
  "Kenya", "Lesotho", "Liberia", "Libye", "Madagascar", "Malawi", "Mali",
  "Maroc", "Maurice", "Mauritanie", "Mozambique", "Namibie", "Niger",
  "Nigeria", "Ouganda", "RDC", "Rwanda", "São Tomé-et-Príncipe", "Sénégal",
  "Seychelles", "Sierra Leone", "Somalie", "Soudan", "Soudan du Sud",
  "Tanzanie", "Tchad", "Togo", "Tunisie", "Zambie", "Zimbabwe"
];

const MembershipPage = () => {
  const { t, language } = useLanguage();
  const [step, setStep] = useState('form');
  const [selectedPlan, setSelectedPlan] = useState('standard');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isLoading, setIsLoading] = useState(false);
  const [premiumRemaining, setPremiumRemaining] = useState(10000);
  const [membersByCountry, setMembersByCountry] = useState([]);
  const [registrationData, setRegistrationData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cryptoHashInput, setCryptoHashInput] = useState('');
  const [paystackEmail, setPaystackEmail] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    profession: '',
    organization: '',
    acceptTerms: false,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [premiumRes, countryRes] = await Promise.all([
        axios.get(`${API}/members/stats/premium-remaining`),
        axios.get(`${API}/members/stats/by-country`)
      ]);
      setPremiumRemaining(premiumRes.data.remaining);
      setMembersByCountry(countryRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!formData.acceptTerms) {
      toast.error('Vous devez accepter les termes et conditions pour continuer.');
      return;
    }
    
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/members/register`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        country: formData.country,
        city: formData.city,
        profession: formData.profession,
        organization: formData.organization,
        membership_type: selectedPlan,
        payment_method: paymentMethod,
        language,
      });

      setRegistrationData(response.data);
      setPaystackEmail(formData.email);

      if (paymentMethod === 'crypto') {
        setStep('crypto');
      } else {
        // Redirect to Paystack for card payment
        const paystackUrl = selectedPlan === 'standard' ? PAYSTACK_STANDARD : PAYSTACK_PREMIUM;
        window.open(paystackUrl, '_blank');
        setStep('payment-pending');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setIsLoading(false);
    }
  };

  // Paystack card payment confirmation with email verification
  const confirmPaystackPayment = async () => {
    if (!paystackEmail) {
      toast.error('Veuillez entrer votre email utilisé pour le paiement');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/members/${registrationData.id}/confirm-payment`, {
        payment_status: 'completed',
        payment_method: 'card',
        transaction_id: 'paystack_' + Date.now(),
        confirmation_email: paystackEmail,
      });
      
      // Send confirmation email
      await axios.post(`${API}/members/${registrationData.id}/send-confirmation-email`);
      
      setStep('success');
      toast.success('Paiement confirmé ! Un email de confirmation vous a été envoyé.');
    } catch (error) {
      toast.error('Erreur lors de la confirmation');
    } finally {
      setIsLoading(false);
    }
  };

  // Crypto payment - submit hash for admin verification
  const submitCryptoHash = async () => {
    if (!cryptoHashInput || cryptoHashInput.length < 10) {
      toast.error('Veuillez entrer un code de transaction valide');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/members/${registrationData.id}/submit-crypto-payment`, {
        transaction_hash: cryptoHashInput,
        wallet_address: CRYPTO_WALLET,
        amount: price,
        currency: 'USDT',
        network: 'TRC20',
      });
      
      setStep('crypto-pending');
      toast.success('Votre paiement a été soumis pour vérification');
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setIsLoading(false);
    }
  };

  const copyWallet = () => {
    navigator.clipboard.writeText(CRYPTO_WALLET);
    setCopied(true);
    toast.success('Adresse copiée !');
    setTimeout(() => setCopied(false), 2000);
  };

  const price = selectedPlan === 'standard' ? 10 : 25;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-gradient-to-br from-oar-blue to-oar-blue-dark overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <div className="bg-white p-2 rounded-lg inline-block mb-8 shadow-lg">
            <img src={OAR_LOGO} alt="OAR Logo" className="h-20 w-20 object-contain" />
          </div>
          <h1
            data-testid="membership-title"
            className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
          >
            {t('membership.heroTitle')}
          </h1>
          <p className="max-w-3xl mx-auto text-lg md:text-xl text-white/90">
            {t('membership.heroSubtitle')}
          </p>
        </div>
      </section>

      {step === 'form' && (
        <>
          {/* Membership Plans */}
          <section className="py-24 bg-oar-bg-secondary">
            <div className="tricolor-line" />
            <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-center text-oar-text-primary mb-16">
                {t('membership.formulas')}
              </h2>

              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* Standard Plan */}
                <div
                  data-testid="plan-standard"
                  onClick={() => setSelectedPlan('standard')}
                  className={`relative bg-white border-2 rounded-lg p-8 cursor-pointer transition-all duration-300 shadow-lg ${
                    selectedPlan === 'standard'
                      ? 'border-oar-blue shadow-oar-blue/20'
                      : 'border-oar-border hover:border-oar-blue/50'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-oar-blue-soft rounded-lg">
                      <GraduationCap className="w-6 h-6 text-oar-blue" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-oar-text-primary">
                        {t('membership.standard.title')}
                      </h3>
                      <p className="text-oar-text-muted text-sm">{t('membership.standard.tagline')}</p>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-5xl font-heading font-bold text-oar-blue">
                      {t('membership.standard.price')}
                    </span>
                    <span className="text-oar-text-muted">{t('membership.standard.period')}</span>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {t('membership.standard.benefits').map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-oar-green flex-shrink-0 mt-0.5" />
                        <span className="text-oar-text-secondary text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-oar-blue-soft border border-oar-blue/20 p-3 rounded-lg">
                    <p className="text-oar-blue text-xs italic">
                      {t('membership.standard.cvPosition')}
                    </p>
                  </div>

                  {selectedPlan === 'standard' && (
                    <div className="absolute top-4 right-4">
                      <Check className="w-6 h-6 text-oar-blue" />
                    </div>
                  )}
                </div>

                {/* Premium Plan */}
                <div
                  data-testid="plan-premium"
                  onClick={() => setSelectedPlan('premium')}
                  className={`relative bg-white border-2 rounded-lg p-8 cursor-pointer transition-all duration-300 shadow-lg ${
                    selectedPlan === 'premium'
                      ? 'border-oar-red shadow-oar-red/20'
                      : 'border-oar-border hover:border-oar-red/50'
                  }`}
                >
                  {/* Premium Badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-oar-red px-4 py-1 rounded-full shadow-lg">
                    <div className="flex items-center gap-1 text-white text-xs font-bold">
                      <Crown className="w-3 h-3" />
                      PREMIUM
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mb-4 mt-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-oar-red-soft rounded-lg">
                      <Crown className="w-6 h-6 text-oar-red" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl text-oar-text-primary">
                        {t('membership.premium.title')}
                      </h3>
                      <p className="text-oar-text-muted text-sm">{t('membership.premium.tagline')}</p>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-5xl font-heading font-bold text-oar-red">
                      {t('membership.premium.price')}
                    </span>
                    <span className="text-oar-text-muted">{t('membership.premium.period')}</span>
                  </div>

                  {/* Limited spots */}
                  <div className="bg-oar-orange-soft border border-oar-orange/30 px-3 py-2 rounded-lg mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-oar-orange text-sm font-medium">{t('membership.premium.limited')}</span>
                      <span className="text-oar-red text-sm font-bold">
                        {premiumRemaining.toLocaleString()} {t('membership.premium.remaining')}
                      </span>
                    </div>
                  </div>

                  <p className="text-oar-red text-sm font-medium mb-3">{t('membership.premium.allStandard')}</p>

                  <ul className="space-y-3 mb-4">
                    {t('membership.premium.benefits').map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Star className="w-5 h-5 text-oar-orange flex-shrink-0 mt-0.5" />
                        <span className="text-oar-text-secondary text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  {selectedPlan === 'premium' && (
                    <div className="absolute top-4 right-4">
                      <Check className="w-6 h-6 text-oar-red" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Why Join Section */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="font-heading text-3xl md:text-4xl font-semibold text-oar-text-primary mb-4">
                    {t('membership.whyJoin.title')}
                  </h2>
                  <p className="text-oar-text-secondary text-lg mb-8">
                    {t('membership.whyJoin.subtitle')}
                  </p>
                  <p className="text-oar-blue font-medium mb-4">{t('membership.whyJoin.positioning')}</p>
                  <ul className="space-y-3">
                    {t('membership.whyJoin.points').map((point, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <ArrowRight className="w-5 h-5 text-oar-orange" />
                        <span className="text-oar-text-secondary">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-oar-bg-secondary border border-oar-border p-8 rounded-lg">
                  <h3 className="font-heading text-xl text-oar-green mb-4">{t('membership.countryBenefits.title')}</h3>
                  <p className="text-oar-text-secondary mb-4">{t('membership.countryBenefits.intro')}</p>
                  <ul className="space-y-3 mb-6">
                    {t('membership.countryBenefits.points').map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Globe className="w-5 h-5 text-oar-green flex-shrink-0 mt-0.5" />
                        <span className="text-oar-text-secondary text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-oar-text-muted text-sm italic">
                    {t('membership.countryBenefits.network')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Registration Form */}
          <section className="py-24 bg-oar-bg-secondary">
            <div className="tricolor-line" />
            <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16">
              <h2 className="font-heading text-3xl font-semibold text-center text-oar-text-primary mb-12">
                {t('membership.form.title')}
              </h2>

              <form onSubmit={handleSubmit} className="bg-white border border-oar-border rounded-lg p-8 shadow-lg">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.firstName')} *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      data-testid="membership-firstname"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.lastName')} *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      data-testid="membership-lastname"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="membership-email"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="membership-phone"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.country')} *
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      data-testid="membership-country"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    >
                      <option value="">Sélectionnez un pays</option>
                      {africanCountries.map((country) => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.city')}
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      data-testid="membership-city"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.profession')}
                    </label>
                    <input
                      type="text"
                      name="profession"
                      value={formData.profession}
                      onChange={handleChange}
                      data-testid="membership-profession"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                  <div>
                    <label className="block text-oar-text-secondary text-sm mb-2">
                      {t('membership.form.organization')}
                    </label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      data-testid="membership-organization"
                      className="w-full bg-oar-bg-secondary border border-oar-border focus:border-oar-blue text-oar-text-primary rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-oar-blue/20"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div className="mb-8">
                  <label className="block text-oar-text-secondary text-sm mb-4">
                    {t('membership.form.paymentMethod')} *
                  </label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      data-testid="payment-card"
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                        paymentMethod === 'card'
                          ? 'border-oar-blue bg-oar-blue-soft'
                          : 'border-oar-border hover:border-oar-blue/50'
                      }`}
                    >
                      <CreditCard className={`w-5 h-5 ${paymentMethod === 'card' ? 'text-oar-blue' : 'text-oar-text-muted'}`} />
                      <span className={paymentMethod === 'card' ? 'text-oar-blue font-medium' : 'text-oar-text-secondary'}>
                        {t('membership.form.card')}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('crypto')}
                      data-testid="payment-crypto"
                      className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                        paymentMethod === 'crypto'
                          ? 'border-oar-orange bg-oar-orange-soft'
                          : 'border-oar-border hover:border-oar-orange/50'
                      }`}
                    >
                      <Wallet className={`w-5 h-5 ${paymentMethod === 'crypto' ? 'text-oar-orange' : 'text-oar-text-muted'}`} />
                      <span className={paymentMethod === 'crypto' ? 'text-oar-orange font-medium' : 'text-oar-text-secondary'}>
                        {t('membership.form.crypto')}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="mb-8">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                      data-testid="accept-terms-checkbox"
                      className="mt-1 w-5 h-5 rounded border-oar-border text-oar-blue focus:ring-oar-blue cursor-pointer"
                      required
                    />
                    <span className="text-sm text-oar-text-secondary leading-relaxed">
                      J'ai lu et j'accepte les{' '}
                      <a 
                        href="/terms" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-oar-blue hover:underline font-medium"
                      >
                        Termes et Conditions d'Utilisation
                      </a>{' '}
                      ainsi que la{' '}
                      <a 
                        href="/privacy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-oar-blue hover:underline font-medium"
                      >
                        Politique de Confidentialité
                      </a>{' '}
                      de l'Organisation Africaine de la Russophonie. Je consens au traitement de mes données personnelles conformément au RGPD. *
                    </span>
                  </label>
                </div>

                {/* Summary */}
                <div className="bg-oar-bg-secondary border border-oar-blue/30 p-6 rounded-lg mb-8">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-oar-text-secondary">
                        {selectedPlan === 'standard' ? t('membership.standard.title') : t('membership.premium.title')}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-3xl font-heading font-bold text-oar-blue">${price}</span>
                      <span className="text-oar-text-muted text-sm"> USD/an</span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="membership-submit"
                  className="w-full flex items-center justify-center gap-2 bg-oar-red hover:bg-oar-red-dark disabled:opacity-50 text-white rounded-lg px-8 py-4 font-medium transition-all duration-300 shadow-lg"
                >
                  {isLoading ? (
                    <span>{t('membership.form.processing')}</span>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      {t('membership.form.submit')} - ${price}
                    </>
                  )}
                </button>
              </form>
            </div>
          </section>

          {/* Members by Country */}
          {membersByCountry.length > 0 && (
            <section className="py-16 bg-white">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <h3 className="font-heading text-2xl text-center text-oar-blue mb-8">
                  {t('membership.membersByCountry')}
                </h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {membersByCountry.slice(0, 10).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 bg-oar-bg-secondary border border-oar-border px-4 py-2 rounded-lg">
                      <MapPin className="w-4 h-4 text-oar-orange" />
                      <span className="text-oar-text-secondary text-sm">{item.country}</span>
                      <span className="text-oar-blue font-bold">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Final Message */}
          <section className="py-24 bg-gradient-to-br from-oar-green to-oar-green-dark">
            <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
              <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">
                {t('membership.finalMessage.title')}
              </h2>
              <p className="text-white/90 text-lg mb-6">
                {t('membership.finalMessage.text')}
              </p>
              <p className="text-oar-orange font-semibold text-xl">
                {t('membership.finalMessage.cta')}
              </p>
            </div>
          </section>
        </>
      )}

      {/* Card Payment Pending Step - with email confirmation */}
      {step === 'payment-pending' && registrationData && (
        <section className="py-24 bg-oar-bg-secondary">
          <div className="max-w-xl mx-auto px-6 md:px-12">
            <div className="bg-white border border-oar-border rounded-lg p-8 text-center shadow-lg">
              <CreditCard className="w-16 h-16 text-oar-blue mx-auto mb-6" />
              <h2 className="font-heading text-2xl text-oar-text-primary mb-4">
                Paiement par carte bancaire
              </h2>
              <p className="text-oar-text-secondary mb-6">
                Une nouvelle fenêtre s'est ouverte vers Paystack pour finaliser votre paiement de <span className="font-bold text-oar-blue">${price} USD</span>.
              </p>
              
              <div className="bg-oar-bg-secondary border border-oar-blue/30 p-4 rounded-lg mb-6">
                <p className="text-oar-text-muted text-sm">
                  Si la fenêtre ne s'est pas ouverte, cliquez sur le lien ci-dessous :
                </p>
                <a
                  href={selectedPlan === 'standard' ? PAYSTACK_STANDARD : PAYSTACK_PREMIUM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-oar-blue font-medium mt-2 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ouvrir Paystack
                </a>
              </div>

              {/* Email confirmation input */}
              <div className="bg-oar-green-soft border border-oar-green/30 p-6 rounded-lg mb-6 text-left">
                <label className="block text-oar-green font-medium mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Confirmez votre email de paiement
                </label>
                <input
                  type="email"
                  value={paystackEmail}
                  onChange={(e) => setPaystackEmail(e.target.value)}
                  placeholder="Votre email utilisé pour le paiement"
                  className="w-full border border-oar-border rounded-lg px-4 py-3 text-oar-text-primary focus:border-oar-green focus:outline-none focus:ring-2 focus:ring-oar-green/20"
                />
                <p className="text-oar-text-muted text-xs mt-2">
                  Un email de confirmation sera envoyé à cette adresse après validation.
                </p>
              </div>

              <button
                onClick={confirmPaystackPayment}
                disabled={isLoading}
                data-testid="confirm-payment"
                className="w-full flex items-center justify-center gap-2 bg-oar-green hover:bg-oar-green-dark disabled:opacity-50 text-white rounded-lg px-8 py-4 font-medium transition-all shadow-lg"
              >
                {isLoading ? (
                  <span>Vérification en cours...</span>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    J'ai effectué le paiement - Confirmer
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Crypto Payment Step - with hash input */}
      {step === 'crypto' && registrationData && (
        <section className="py-24 bg-oar-bg-secondary">
          <div className="max-w-xl mx-auto px-6 md:px-12">
            <div className="bg-white border border-oar-border rounded-lg p-8 text-center shadow-lg">
              <Wallet className="w-16 h-16 text-oar-orange mx-auto mb-6" />
              <h2 className="font-heading text-2xl text-oar-text-primary mb-4">
                Paiement en Cryptomonnaie
              </h2>
              <p className="text-oar-text-secondary mb-6">
                Envoyez exactement <span className="font-bold text-oar-red">${price} USDT</span> à l'adresse ci-dessous.
              </p>

              <div className="bg-oar-orange-soft border border-oar-orange/30 p-6 rounded-lg mb-6">
                <p className="text-oar-orange text-sm font-bold mb-2">Réseau: USDT TRC20 (Tron)</p>
                
                <p className="text-oar-text-muted text-sm mb-2">Adresse du portefeuille:</p>
                <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-oar-border">
                  <code className="text-oar-blue text-xs md:text-sm flex-1 break-all font-mono">{CRYPTO_WALLET}</code>
                  <button
                    onClick={copyWallet}
                    className="p-2 hover:bg-oar-blue-soft rounded-lg transition-colors flex-shrink-0"
                  >
                    {copied ? <Check className="w-5 h-5 text-oar-green" /> : <Copy className="w-5 h-5 text-oar-blue" />}
                  </button>
                </div>

                {/* QR Code */}
                <div className="mt-6">
                  <img
                    src={CRYPTO_QR_CODE}
                    alt="USDT TRC20 QR Code"
                    className="w-48 h-48 mx-auto rounded-lg border border-oar-border"
                  />
                  <p className="text-oar-text-muted text-xs mt-2">Scannez le QR code pour payer</p>
                </div>
              </div>

              {/* Transaction Hash Input */}
              <div className="bg-oar-red-soft border border-oar-red/30 p-6 rounded-lg mb-6 text-left">
                <label className="block text-oar-red font-medium mb-2 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Code de transaction (Hash) *
                </label>
                <input
                  type="text"
                  value={cryptoHashInput}
                  onChange={(e) => setCryptoHashInput(e.target.value)}
                  placeholder="Ex: 0x1234567890abcdef..."
                  data-testid="crypto-hash-input"
                  className="w-full border border-oar-border rounded-lg px-4 py-3 text-oar-text-primary font-mono text-sm focus:border-oar-orange focus:outline-none focus:ring-2 focus:ring-oar-orange/20"
                />
                <div className="flex items-start gap-2 mt-3">
                  <AlertCircle className="w-4 h-4 text-oar-orange flex-shrink-0 mt-0.5" />
                  <p className="text-oar-text-muted text-xs">
                    Après avoir envoyé le paiement, copiez le hash de transaction (TXID) depuis votre portefeuille ou l'explorateur blockchain et collez-le ci-dessus. 
                    <strong className="text-oar-red"> Un administrateur vérifiera votre paiement manuellement.</strong>
                  </p>
                </div>
              </div>

              <button
                onClick={submitCryptoHash}
                disabled={isLoading || !cryptoHashInput}
                data-testid="submit-crypto-hash"
                className="w-full flex items-center justify-center gap-2 bg-oar-orange hover:bg-oar-orange-dark disabled:opacity-50 text-white rounded-lg px-8 py-4 font-medium transition-all shadow-lg"
              >
                {isLoading ? (
                  <span>Soumission en cours...</span>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    Soumettre pour vérification
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Crypto Payment Pending Verification */}
      {step === 'crypto-pending' && registrationData && (
        <section className="py-24 bg-oar-bg-secondary">
          <div className="max-w-xl mx-auto px-6 md:px-12">
            <div className="bg-white border border-oar-orange/30 rounded-lg p-8 text-center shadow-lg">
              <div className="w-20 h-20 bg-oar-orange-soft rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-oar-orange" />
              </div>
              <h2 className="font-heading text-3xl text-oar-text-primary mb-4">
                Paiement en attente de vérification
              </h2>
              <p className="text-oar-text-secondary mb-6">
                Votre paiement crypto a été soumis pour vérification par notre équipe administrative.
              </p>
              
              <div className="bg-oar-bg-secondary border border-oar-border p-4 rounded-lg mb-6">
                <p className="text-oar-text-muted text-sm">
                  Numéro de référence:
                </p>
                <p className="text-oar-blue font-heading text-xl font-bold mt-1">
                  {registrationData.member_number}
                </p>
              </div>

              <div className="bg-oar-green-soft border border-oar-green/30 p-4 rounded-lg">
                <p className="text-oar-green text-sm">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  Vous recevrez un email de confirmation une fois votre paiement validé par un administrateur.
                </p>
              </div>

              <p className="text-oar-text-muted text-xs mt-6">
                Délai de traitement: généralement sous 24-48 heures ouvrées.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Success Step */}
      {step === 'success' && registrationData && (
        <section className="py-24 bg-oar-bg-secondary">
          <div className="max-w-xl mx-auto px-6 md:px-12">
            <div className="bg-white border border-oar-green/30 rounded-lg p-8 text-center shadow-lg">
              <div className="w-20 h-20 bg-oar-green-soft rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-oar-green" />
              </div>
              <h2 className="font-heading text-3xl text-oar-text-primary mb-4">
                {t('membership.success.title')}
              </h2>
              <p className="text-oar-text-secondary mb-6">
                {t('membership.success.memberNumber')}
              </p>
              
              {/* Matricule Display */}
              <div className="bg-oar-orange-soft border border-oar-orange/30 p-4 rounded-lg mb-4">
                <p className="text-oar-text-muted text-xs mb-1">Votre matricule</p>
                <span className="text-oar-orange font-mono text-2xl font-bold tracking-wider">
                  {registrationData.matricule}
                </span>
              </div>
              
              {/* Member Number Display */}
              <div className="bg-oar-blue-soft border border-oar-blue/30 p-4 rounded-lg mb-6">
                <p className="text-oar-text-muted text-xs mb-1">Numéro de membre</p>
                <span className="text-oar-blue font-heading text-xl font-bold">
                  {registrationData.member_number}
                </span>
              </div>
              
              <div className="bg-oar-green-soft border border-oar-green/30 p-4 rounded-lg mb-6">
                <p className="text-oar-green text-sm flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Un email de confirmation a été envoyé à votre adresse.
                </p>
              </div>
              
              {/* Download Buttons */}
              <div className="space-y-3 mb-6">
                <p className="text-oar-text-secondary text-sm font-medium mb-3">
                  Téléchargez vos documents de membre :
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href={`${API}/members/${registrationData.id}/certificate`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-certificate-btn"
                    className="flex items-center justify-center gap-2 bg-oar-blue hover:bg-oar-blue-dark text-white rounded-lg px-4 py-3 font-medium transition-all shadow-md"
                  >
                    <FileText className="w-5 h-5" />
                    Certificat PDF
                  </a>
                  <a
                    href={`${API}/members/${registrationData.id}/card`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-card-btn"
                    className="flex items-center justify-center gap-2 bg-oar-orange hover:bg-oar-orange-dark text-white rounded-lg px-4 py-3 font-medium transition-all shadow-md"
                  >
                    <CreditCard className="w-5 h-5" />
                    Carte de membre
                  </a>
                </div>
              </div>
              
              <p className="text-oar-text-muted text-xs">
                Conservez précieusement votre matricule. Il vous identifie comme membre officiel de l'OAR.
              </p>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default MembershipPage;
